import { useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, useFetch } from "../components/Shared";
import { ImpactBadge, StatusBadge, ProtectedBadge } from "../lib/supervisorUi";
import { Badge } from "../components/Shared";
import { Check, X, PencilSimple, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";

function RecCard({ r, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState(r.proposed_prompt || "");
  const [busy, setBusy] = useState("");
  const isPrompt = r.type === "PROMPT" || r.type === "BEHAVIOR";
  const done = ["IMPLEMENTED", "APPROVED", "REJECTED", "ROLLED_BACK"].includes(r.status);

  const act = async (verb, body) => {
    setBusy(verb);
    try {
      await api.post(`/supervisor/recommendations/${r.id}/${verb}`, body || {});
      toast.success(verb === "approve" ? "Recomendación aprobada" : verb === "reject" ? "Recomendación rechazada" : "Regenerada");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally { setBusy(""); }
  };

  return (
    <Card className="p-5" testid={`rec-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <Badge tone="neutral">{r.type}</Badge>
            {r.requires_direction_approval && <ProtectedBadge />}
            <StatusBadge value={r.status} />
          </div>
          <h3 className="font-heading text-lg">{r.title}</h3>
          <div className="text-xs text-carbon-500 mt-0.5">Agente: {r.agent_name || "—"}</div>
        </div>
        <div className="text-right text-xs text-carbon-500 shrink-0">
          <div>Confianza: <b className="text-carbon-800">{r.confidence_score ?? "—"}%</b></div>
          <div className="flex gap-2 mt-1">Impacto <ImpactBadge value={r.estimated_impact} /></div>
          <div className="flex gap-2 mt-1 justify-end">Riesgo <ImpactBadge value={r.risk_level} /></div>
        </div>
      </div>

      {r.description && <p className="text-sm text-carbon-600 mb-3">{r.description}</p>}
      {r.evidence && <p className="text-xs text-carbon-500 mb-3">Evidencia: {r.evidence} ({r.evidence_count} conversaciones)</p>}

      {isPrompt && (r.current_prompt || r.proposed_prompt) && (
        <div className="grid md:grid-cols-2 gap-3 mb-3">
          <div>
            <div className="section-label mb-1">Antes</div>
            <pre className="bg-red-50 border border-signal/20 rounded p-3 text-xs whitespace-pre-wrap font-mono max-h-48 overflow-auto">{r.current_prompt || "(sin fragmento)"}</pre>
          </div>
          <div>
            <div className="section-label mb-1">Después</div>
            {editing ? (
              <textarea data-testid={`rec-edit-${r.id}`} value={edited} onChange={(e) => setEdited(e.target.value)}
                rows={7} className="w-full bg-forest-light border border-forest/30 rounded p-3 text-xs font-mono" />
            ) : (
              <pre className="bg-forest-light border border-forest/20 rounded p-3 text-xs whitespace-pre-wrap font-mono max-h-48 overflow-auto">{r.proposed_prompt || "(sin propuesta)"}</pre>
            )}
          </div>
        </div>
      )}

      {r.requires_direction_approval && (
        <div className="bg-red-50 border border-signal/30 rounded p-3 text-xs text-signal mb-3">
          Toca la regla protegida «{r.protected_rule_name}». Requiere aprobación explícita de Dirección HM — no se aplica automáticamente.
        </div>
      )}

      {!done && (
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid={`rec-approve-${r.id}`} onClick={() => act("approve", editing ? { edited_proposed: edited } : {})}
            disabled={busy} className="bg-forest text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:opacity-90 disabled:opacity-60">
            <Check size={14} /> Aprobar
          </button>
          <button data-testid={`rec-reject-${r.id}`} onClick={() => act("reject", { reason: "Rechazada por el administrador" })}
            disabled={busy} className="bg-white border border-carbon-200 text-signal px-4 py-2 rounded text-sm flex items-center gap-2 hover:border-signal disabled:opacity-60">
            <X size={14} /> Rechazar
          </button>
          {isPrompt && (
            <button data-testid={`rec-edit-btn-${r.id}`} onClick={() => setEditing((v) => !v)}
              className="bg-white border border-carbon-200 text-carbon-800 px-4 py-2 rounded text-sm flex items-center gap-2 hover:border-terracotta">
              <PencilSimple size={14} /> {editing ? "Cancelar edición" : "Editar"}
            </button>
          )}
          <button data-testid={`rec-regen-${r.id}`} onClick={() => act("regenerate")}
            disabled={busy} className="bg-white border border-carbon-200 text-carbon-800 px-4 py-2 rounded text-sm flex items-center gap-2 hover:border-terracotta disabled:opacity-60">
            <ArrowsClockwise size={14} /> Nueva propuesta
          </button>
        </div>
      )}
      {r.status === "IMPLEMENTED" && r.implemented_version && (
        <div className="text-xs text-forest">✓ Implementada · versión v{r.implemented_version} en producción</div>
      )}
    </Card>
  );
}

export default function SupervisorRecommendations() {
  const [status, setStatus] = useState("PENDING_REVIEW");
  const { data, loading, reload } = useFetch(
    () => api.get("/supervisor/recommendations", { params: status ? { status } : {} }).then((r) => r.data),
    [status]
  );
  const items = data?.items || [];

  return (
    <>
      <PageHeader label="Inteligencia Conversacional" title="Recomendaciones"
        description="Propuestas de mejora. Ninguna se aplica sin aprobación humana.">
        <select data-testid="rec-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-white border border-carbon-200 rounded px-3 py-2 text-sm">
          <option value="">Todas</option>
          <option value="PENDING_REVIEW">Pendientes</option>
          <option value="IMPLEMENTED">Implementadas</option>
          <option value="APPROVED">Aprobadas</option>
          <option value="REJECTED">Rechazadas</option>
        </select>
      </PageHeader>
      <PageBody>
        {loading ? <div className="text-carbon-500">Cargando...</div> : items.length === 0 ? (
          <Card className="p-10 text-center text-carbon-500">Sin recomendaciones en este estado.</Card>
        ) : (
          <div className="space-y-4" data-testid="rec-list">
            {items.map((r) => <RecCard key={r.id} r={r} onChanged={reload} />)}
          </div>
        )}
      </PageBody>
    </>
  );
}
